import { faEnvelope, faLockAlt } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "eone-ui/buttons/button";
import { FormButton } from "eone-ui/forms/form-button";
import { FormError } from "eone-ui/forms/form-error";
import { FormSelect } from "eone-ui/forms/form-select";
import { FormString } from "eone-ui/forms/form-string";
import React, { useState, useEffect } from "react";
import { productName } from "../shared/product";

// Humanized descriptions for scopes worth showing a human. Scopes without an entry (e.g. openid —
// "you are signing in" is what the form already says) are hidden. Extend as richer-scoped
// dynamic clients arrive; unknown scopes are never rendered raw.
const SCOPE_DESCRIPTIONS = {
    mcp: `Connect to and use your ${productName} MCP server`,
    user: "Read and manage your user and account information",
};

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [error, setError] = useState("");
    // setKmsi is unused while the "Keep me signed in" checkbox is commented out below; kmsi still
    // rides along in the /token/login body as false.
    // eslint-disable-next-line no-unused-vars
    const [kmsi, setKmsi] = useState(false);
    const [accountSelection, setAccountSelection] = useState(null);
    const [selectedAccountId, setSelectedAccountId] = useState("");

    const urlParams = new URLSearchParams(window.location.search);

    const isOAuth2Mode = () => urlParams.has("client_id");

    // "Create an account" is shown when the relying party opts in via allow_register=1
    // (/authorize threads it from the client's AllowRegistration flag). Display-only,
    // not a gate: /register stays reachable directly.
    const showCreateAccount = !isOAuth2Mode() || urlParams.get("allow_register") === "1";

    // Relying-party display (dynamically-registered clients only): /authorize appends
    // client_name + display_scope (the client-level-narrowed grant) to the login URL.
    const rpClientName = urlParams.get("client_name");
    const rpScopeLines = (urlParams.get("display_scope") || "")
        .split(" ")
        .map((s) => SCOPE_DESCRIPTIONS[s])
        .filter(Boolean);

    useEffect(() => {
        setLoggingIn(false);
        if (urlParams.has("error")) {
            setError("Authentication failed. Please check your credentials and try again.");
        } else if (urlParams.has("signin_error")) {
            setError("We couldn't complete your sign-in. Please try signing in again.");
        }
    }, []);

    useEffect(() => {
        const handlePageShow = (e) => {
            if (e.persisted) setLoggingIn(false);
        };
        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    }, []);

    const logIn = async () => {
        setLoggingIn(true);

        const accountId = urlParams.get("accountid") || undefined;
        const accountName = urlParams.get("account") || undefined;

        try {
            if (isOAuth2Mode()) {
                const body = {
                    email,
                    password,
                    clientId: urlParams.get("client_id"),
                    redirectUri: urlParams.get("redirect_uri"),
                    scope: urlParams.get("scope"),
                    state: urlParams.get("state"),
                    codeChallenge: urlParams.get("code_challenge"),
                    codeChallengeMethod: urlParams.get("code_challenge_method"),
                    nonce: urlParams.get("nonce"),
                    resource: urlParams.get("resource"),
                    kmsi,
                    authFlowId: urlParams.get("auth_flow_id"),
                    eqa: urlParams.get("eqa") === "true",
                    ...(accountId && { accountId }),
                    ...(accountName && { accountName }),
                };
                const response = await fetch("/token/login", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(body),
                });

                if (response.status === 200) {
                    const data = await response.json();
                    if (data.requiresAccountSelection) {
                        const accounts = data.accounts || [];
                        setAccountSelection({ token: data.selectionToken, accounts });
                        setSelectedAccountId(accounts[0]?.accountId || "");
                        setLoggingIn(false);
                    } else if (data.requiresStepUp) {
                        window.location.href = `/enter-code/?token=${encodeURIComponent(data.token)}`;
                    } else if (data.requiresMfaSetup) {
                        window.location.href = `/mfa-setup/?token=${encodeURIComponent(data.setupToken)}`;
                    } else if (data.requiresMfa) {
                        const params = new URLSearchParams();
                        params.set("email", email);
                        if (urlParams.get("client_id")) params.set("client_id", urlParams.get("client_id"));
                        if (urlParams.get("redirect_uri")) params.set("redirect_uri", urlParams.get("redirect_uri"));
                        if (urlParams.get("scope")) params.set("scope", urlParams.get("scope"));
                        if (urlParams.get("state")) params.set("state", urlParams.get("state"));
                        if (urlParams.get("code_challenge")) params.set("code_challenge", urlParams.get("code_challenge"));
                        if (urlParams.get("code_challenge_method")) params.set("code_challenge_method", urlParams.get("code_challenge_method"));
                        if (urlParams.get("nonce")) params.set("nonce", urlParams.get("nonce"));
                        // resource (RFC 8707) rides the whole flow — dropping it here mints a
                        // client_id-audienced token that resource servers reject.
                        if (urlParams.get("resource")) params.set("resource", urlParams.get("resource"));
                        if (urlParams.get("auth_flow_id")) params.set("auth_flow_id", urlParams.get("auth_flow_id"));
                        // eqa rides the whole flow so the MFA step re-requests EQA-region routing.
                        if (urlParams.get("eqa") === "true") params.set("eqa", "true");
                        // mfaChallengeToken is required by /token/login-mfa to prevent brute-force
                        if (data.mfaChallengeToken) params.set("mfa_challenge_token", data.mfaChallengeToken);
                        // Note: accountid/account hints are not forwarded through the MFA flow
                        sessionStorage.setItem("oauth2_kmsi", kmsi ? "true" : "false");
                        window.location.href = `/enter-code/?${params.toString()}`;
                    } else {
                        window.location.href = data.redirectUrl;
                    }
                } else {
                    const data = await response.json();
                    if (data.error === "session_expired") {
                        // auth_flow cookie expired — re-trigger /authorize with the same params
                        // so a fresh cookie is issued and the user lands back here transparently.
                        const authorizeParams = new URLSearchParams();
                        authorizeParams.set("response_type", urlParams.get("response_type") || "code");
                        authorizeParams.set("client_id", urlParams.get("client_id") || "");
                        if (urlParams.get("redirect_uri")) authorizeParams.set("redirect_uri", urlParams.get("redirect_uri"));
                        if (urlParams.get("scope")) authorizeParams.set("scope", urlParams.get("scope"));
                        if (urlParams.get("state")) authorizeParams.set("state", urlParams.get("state"));
                        if (urlParams.get("code_challenge")) authorizeParams.set("code_challenge", urlParams.get("code_challenge"));
                        if (urlParams.get("code_challenge_method")) authorizeParams.set("code_challenge_method", urlParams.get("code_challenge_method"));
                        if (urlParams.get("nonce")) authorizeParams.set("nonce", urlParams.get("nonce"));
                        if (urlParams.get("resource")) authorizeParams.set("resource", urlParams.get("resource"));
                        window.location.href = `/authorize?${authorizeParams.toString()}`;
                        return;
                    }
                    setError(data.error_description);
                    setLoggingIn(false);
                }
            } else {
                const eqa = urlParams.get("eqa") === "true";
                const response = await fetch("/api/login", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email, password, kmsi, accountId, accountName, eqa }),
                });
                if (response.status === 200) {
                    const data = await response.json();
                    if (data.requiresMfaSetup) {
                        window.location.href = `/mfa-setup/?token=${encodeURIComponent(data.setupToken)}`;
                    } else if (data.requiresMfa) {
                        const mfaQuery = eqa
                            ? `email=${encodeURIComponent(email)}&eqa=true`
                            : `email=${encodeURIComponent(email)}`;
                        window.location.href = `/enter-code/?${mfaQuery}`;
                    } else {
                        window.location.href = data.redirectUrl;
                    }
                } else {
                    const data = await response.json();
                    setError(data.error_description);
                    setLoggingIn(false);
                }
            }
        } catch (e) {
            setError("An unexpected error occurred. Please try again.");
            setLoggingIn(false);
        }
    };

    const selectAccount = async () => {
        if (!accountSelection || !selectedAccountId) return;
        setLoggingIn(true);
        setError("");
        try {
            const response = await fetch("/token/select-account", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token: accountSelection.token, accountId: selectedAccountId }),
            });
            const data = await response.json();
            if (response.ok) {
                window.location.href = data.redirectUrl;
                return;
            }
            setError(data.error_description || "We couldn't select that account. Please sign in again.");
        } catch (e) {
            setError("An unexpected error occurred. Please try again.");
        }
        setLoggingIn(false);
    };

    const getButtonLabel = () => {
        if (loggingIn) return accountSelection ? "Continuing..." : "Logging in...";
        return accountSelection ? "Continue" : "Log in";
    };

    const isButtonEnabled = () => accountSelection
        ? selectedAccountId && !loggingIn
        : email && password && !loggingIn;

    const accountOptions = (accountSelection?.accounts || []).map((account) => ({
        Key: account.accountId,
        Value: account.name || "Account",
    }));

    return (
        <div className="eone-login-form-container">
            <label className="eone-login-form-title">{accountSelection ? "Select an account" : "Log in"}</label>
            {!accountSelection && rpClientName && (
                <div className="eone-login-client-info" data-testid="login_clientInfo">
                    <label><b>{rpClientName}</b> is requesting access to your {productName} account.</label>
                    {rpScopeLines.length > 0 && (
                        <>
                            <label>This will allow {rpClientName} to:</label>
                            <ul>
                                {rpScopeLines.map((line) => <li key={line}>{line}</li>)}
                            </ul>
                        </>
                    )}
                </div>
            )}
            <div className="eone-login-form">
                {accountSelection ? (
                    <FormSelect
                        Label="Account"
                        Value={selectedAccountId}
                        Options={accountOptions}
                        OnChange={setSelectedAccountId}
                        DataTestId="login_account" />
                ) : (
                    <>
                        <FormString
                            Label="Email"
                            Icon={faEnvelope}
                            Location="Login"
                            AutoComplete="email"
                            Value={email}
                            OnChange={setEmail}
                            DataTestId={"login_email"}/>
                        <FormString
                            Label="Password"
                            Password
                            Icon={faLockAlt}
                            Location={"Login"}
                            AutoComplete="password"
                            Value={password}
                            OnChange={setPassword}
                            OnEnter={logIn}
                            DataTestId={"login_password"}/>
                    </>
                )}
                <FormError Visible={error} Message={error} DataTestId={"loginError"} />
                {/* KMSI disabled — uncomment to re-enable "Keep me signed in" (also remove the persistent=false line in eOne.Login/Services/SsoCookieService.cs)
                <div className="eone-login-kmsi">
                    <input
                        type="checkbox"
                        id="kmsi"
                        checked={kmsi}
                        onChange={(e) => setKmsi(e.target.checked)}
                        data-testid="login_kmsi" />
                    <label htmlFor="kmsi">Keep me signed in</label>
                </div>
                */}
            </div>
            <FormButton
                Label={getButtonLabel()}
                Class="eone-login-form-button"
                Location={"Login"}
                Disabled={!isButtonEnabled()}
                OnClick={accountSelection ? selectAccount : logIn}
                DataTestId={"login_submit"}/>
            {!accountSelection && <div className="eone-login-form-link-container">
                <Button
                    OnClick={() => {
                        const forgotParams = new URLSearchParams(window.location.search);
                        if (email) forgotParams.set("email", email);
                        window.location.href = "/forgot-password/?" + forgotParams.toString();
                    }}
                    DataTestId="login_forgotPassword"
                    Label="Forgot password?" />
                {showCreateAccount && (
                    <div className="eone-login-form-link">
                        <label>Don't have an account?</label>
                        <Button
                            OnClick={() => { window.location.href = "/register/" + window.location.search; }}
                            DataTestId="login_createAnAccount"
                            Label="Create an account" />
                    </div>
                )}
            </div>}
        </div>
    );
};

export { Login };
